/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import {
  Checkbox,
  ListItemText,
  MenuItem,
  Select as MaterialSelect,
  SelectChangeEvent,
} from '@mui/material';
import { SxProps, useTheme } from '@mui/material/styles';
import { SetterOrUpdater } from 'recoil';
import { SGTooltip } from '../core';
import { TooltipProps } from '@mui/material/Tooltip';
import { useState } from 'react';

interface SGSelectProps {
  disabled?: boolean;
  label: string;
  value: any;
  setter: SetterOrUpdater<any>;
  options: any[];
  dontRenderAllSelected?: boolean;
  optionsTextTransform?: (val: any) => string;
  valueTextTransform?: (val: any) => string;
  tooltipTransform?: (val: any) => string | undefined;
  menuItemStyleTransform?: (val: any) => any;
  sx?: SxProps;
  iconSx?: SxProps;
  multiple?: boolean;
  selectTooltipProps?: Partial<TooltipProps>;
}

export const SGSelect = ({
  disabled,
  label,
  value,
  setter,
  options,
  optionsTextTransform,
  valueTextTransform,
  tooltipTransform,
  menuItemStyleTransform,
  selectTooltipProps,
  dontRenderAllSelected = false,
  sx = {},
  iconSx = {},
  multiple = true,
}: SGSelectProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [hovering, setHovering] = useState(false);

  const onChange = (event: SelectChangeEvent<any>) => {
    const {
      target: { value },
    } = event;
    setter(multiple && typeof value === 'string' ? value.split(',') : value);
  };

  const toValueText = (option: any) =>
    valueTextTransform
      ? valueTextTransform(option)
      : optionsTextTransform
        ? optionsTextTransform(option)
        : option;

  const toOptionsText = (option: any) =>
    optionsTextTransform ? optionsTextTransform(option) : option;

  const tooltipProps: any =
    open || !hovering
      ? {}
      : {
          title:
            multiple && dontRenderAllSelected && value.length > 0
              ? value.map(toValueText).join(', ')
              : undefined,
          placement: 'top',
          ...(selectTooltipProps ?? {}),
        };
  return (
    <SGTooltip {...tooltipProps}>
      <MaterialSelect
        className="canvas-no-drag"
        disabled={disabled ?? false}
        multiple={multiple}
        value={value}
        displayEmpty
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setHovering(false);
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        size="small"
        sx={{
          fontSize: 11,
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '.MuiSelect-icon': {
            color: theme.palette.primary.main,
            ...(iconSx as any),
          },
          ...sx,
        }}
        renderValue={(selected) => {
          if (!Array.isArray(selected)) {
            return toValueText(selected);
          }

          if (selected.length === 0) {
            return label ?? `Click to select`;
          }
          if (!dontRenderAllSelected) {
            return selected.map(toValueText).join(' + ');
          }
          return value.length >= options.length
            ? 'Selected All'
            : `Selected ${value.length} of ${options.length}`;
        }}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
      >
        {options.map((option) => {
          return (
            <MenuItem
              key={option}
              value={option}
              sx={{
                fontSize: 10,
                py: 0,
                px: 4,
                ...(menuItemStyleTransform?.(option) ?? {}),
              }}
            >
              {multiple && <Checkbox checked={value.indexOf(option) > -1} />}
              <SGTooltip
                title={tooltipTransform ? tooltipTransform(option) : undefined}
                placement="left"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 30], // offset some px left to avoid covering checkbox
                        },
                      },
                    ],
                  },
                }}
              >
                <ListItemText
                  sx={
                    multiple
                      ? {}
                      : {
                          paddingY: '10px',
                        }
                  }
                >
                  {toOptionsText(option)}
                </ListItemText>
              </SGTooltip>
            </MenuItem>
          );
        })}
      </MaterialSelect>
    </SGTooltip>
  );
};
